import { create } from 'zustand';
import type { FileIndexEntry } from '../services/backend/types';
import type { IBackend } from '../services/backend/IBackend';
import { CacheKeys, envelope, readCache, writeCache, type CacheEnvelope } from '@/lib/projectCache';
import { useCacheStatusStore } from './cacheStatusStore';

const INDEX_CACHE_VERSION = 1;

interface FileIndexState {
  /** Flat list of all files/folders in the project */
  entries: FileIndexEntry[];
  /** Whether indexing is in progress */
  isIndexing: boolean;
  /** Project ID that was indexed */
  projectId: string | null;

  /** Build the index for a project (async, non-blocking) */
  buildIndex: (backend: IBackend, projectId: string) => Promise<void>;
  /** Search the index by name or path (synchronous) */
  search: (query: string, limit?: number) => FileIndexEntry[];
  /** Clear the index (on project switch) */
  clear: () => void;
}

export const useFileIndexStore = create<FileIndexState>((set, get) => ({
  entries: [],
  isIndexing: false,
  projectId: null,

  buildIndex: async (backend, projectId) => {
    // Skip if already indexing the same project
    if (get().isIndexing && get().projectId === projectId) return;

    set({ projectId });

    // 1. Instant fill from cache so Cmd+P feels usable immediately.
    void (async () => {
      const cached = await readCache<CacheEnvelope<FileIndexEntry[]>>(
        projectId,
        CacheKeys.fileIndex,
      );
      if (!cached || cached.schemaVersion !== INDEX_CACHE_VERSION) return;
      if (get().projectId !== projectId) return;
      // Don't overwrite if a fresh index has already arrived (rare race).
      if (get().entries.length === 0) {
        set({ entries: cached.payload });
      }
    })();

    // 2. Refresh from disk in the background and persist the new snapshot.
    set({ isIndexing: true });
    useCacheStatusStore.getState().begin('file-index');
    try {
      const entries = await backend.files.indexProjectFiles(projectId);
      if (get().projectId === projectId) {
        set({ entries, isIndexing: false });
        await writeCache(projectId, CacheKeys.fileIndex, envelope(entries, INDEX_CACHE_VERSION));
      }
    } catch (err) {
      console.warn('File indexing failed:', err);
      if (get().projectId === projectId) {
        set({ isIndexing: false });
      }
    } finally {
      useCacheStatusStore.getState().end('file-index');
    }
  },

  search: (query, limit = 50) => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase().replace(/\\/g, '/');
    const entries = get().entries;

    const results: FileIndexEntry[] = [];
    for (const entry of entries) {
      if (results.length >= limit) break;
      const lowerName = entry.name.toLowerCase();
      const lowerPath = entry.path.toLowerCase().replace(/\\/g, '/');
      if (lowerName.includes(lowerQuery) || lowerPath.includes(lowerQuery)) {
        results.push(entry);
      }
    }
    return results;
  },

  clear: () => {
    set({ entries: [], isIndexing: false, projectId: null });
  },
}));
